import { existsSync } from 'node:fs';
import { readFile } from 'node:fs/promises';
import {
  dirname,
  extname,
  isAbsolute,
  join,
  normalize,
  posix,
  relative,
  resolve,
  sep,
} from 'node:path';

/**
 * Lightweight import scanner. Resolves relative import/require/dynamic-import
 * specifiers in TS/JS files to project-relative file paths. Bare specifiers,
 * Node built-ins, and aliases other than the tsconfig "@/" alias are ignored.
 */

const SOURCE_EXTENSIONS = new Set(['.ts', '.tsx', '.js', '.jsx', '.mjs', '.cjs']);
const RESOLUTION_EXTENSIONS = ['.ts', '.tsx', '.js', '.jsx', '.mjs', '.cjs'] as const;

const IMPORT_PATTERNS: readonly RegExp[] = [
  // import X from '...'; import {a} from "..."; import '...'; import type X from '...'
  /\bimport\s+(?:[^'";]+\s+from\s+)?["']([^"']+)["']/g,
  // export {a} from '...'; export * from '...'
  /\bexport\s+(?:\*|\{[^}]*\})\s+from\s+["']([^"']+)["']/g,
  // import('...')
  /\bimport\(\s*["']([^"']+)["']\s*\)/g,
  // require('...')
  /\brequire\(\s*["']([^"']+)["']\s*\)/g,
];

export interface ImportEdgeResolved {
  /** Project-relative source file (forward slashes). */
  from: string;
  /** Project-relative target file (forward slashes). */
  to: string;
}

export interface ImportScannerOptions {
  projectRoot: string;
  /** Project-relative file paths to scan. */
  files: string[];
  /** Optional path alias map, e.g. { "@/": "src/" }. Values are project-relative. */
  aliases?: Record<string, string>;
}

function toForward(p: string): string {
  return p.split(sep).join('/');
}

function toProjectRelative(projectRoot: string, abs: string): string {
  const rel = relative(projectRoot, abs);
  return rel === '' ? '.' : toForward(rel);
}

function isSourceFile(relPath: string): boolean {
  return SOURCE_EXTENSIONS.has(extname(relPath).toLowerCase());
}

function resolveWithExtensions(absCandidate: string): string | null {
  if (existsSync(absCandidate)) {
    const ext = extname(absCandidate).toLowerCase();
    if (SOURCE_EXTENSIONS.has(ext)) return absCandidate;
  }
  for (const ext of RESOLUTION_EXTENSIONS) {
    const withExt = absCandidate + ext;
    if (existsSync(withExt)) return withExt;
  }
  for (const ext of RESOLUTION_EXTENSIONS) {
    const indexFile = join(absCandidate, 'index' + ext);
    if (existsSync(indexFile)) return indexFile;
  }
  return null;
}

function stripExplicitExtension(spec: string): string {
  // import paths often use .js even when source is .ts — strip the explicit ext.
  const ext = extname(spec).toLowerCase();
  if (ext === '.js' || ext === '.mjs' || ext === '.cjs') {
    return spec.slice(0, -ext.length);
  }
  return spec;
}

export function extractImportSpecifiers(content: string): string[] {
  const specs: string[] = [];
  for (const pattern of IMPORT_PATTERNS) {
    pattern.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = pattern.exec(content)) !== null) {
      specs.push(m[1]!);
    }
  }
  return specs;
}

function resolveSpecifier(
  spec: string,
  fromAbsFile: string,
  projectRoot: string,
  aliases: Record<string, string>,
): string | null {
  // Bare specifiers (node:, package names) → ignore.
  if (spec.startsWith('node:')) return null;
  // Aliases first.
  for (const [alias, target] of Object.entries(aliases)) {
    if (spec === alias.replace(/\/$/, '') || spec.startsWith(alias)) {
      const remainder = spec.slice(alias.length);
      const aliasedAbs = join(projectRoot, target, remainder);
      const resolved = resolveWithExtensions(stripExplicitExtension(aliasedAbs));
      return resolved ? toProjectRelative(projectRoot, resolved) : null;
    }
  }
  if (spec.startsWith('.') || isAbsolute(spec)) {
    const base = isAbsolute(spec) ? spec : resolve(dirname(fromAbsFile), spec);
    const resolved = resolveWithExtensions(stripExplicitExtension(base));
    return resolved ? toProjectRelative(projectRoot, resolved) : null;
  }
  return null;
}

export async function scanImports(options: ImportScannerOptions): Promise<ImportEdgeResolved[]> {
  const projectRoot = options.projectRoot;
  const aliases = options.aliases ?? {};
  const edges: ImportEdgeResolved[] = [];
  const seen = new Set<string>();
  for (const relPath of options.files) {
    if (!isSourceFile(relPath)) continue;
    const absFile = join(projectRoot, relPath);
    let content: string;
    try {
      content = await readFile(absFile, 'utf8');
    } catch {
      continue;
    }
    const specs = extractImportSpecifiers(content);
    for (const spec of specs) {
      const target = resolveSpecifier(spec, absFile, projectRoot, aliases);
      if (!target) continue;
      const key = relPath + ' ' + target;
      if (seen.has(key)) continue;
      seen.add(key);
      edges.push({ from: normalize(relPath).split(sep).join(posix.sep), to: target });
    }
  }
  return edges;
}
